import { useState, FormEvent } from "react";

const serviceOptions = [
  "Tree Trimming / Pruning",
  "Tree Removal",
  "Stump Grinding",
  "Emergency Tree Service",
  "Not Sure / Multiple Services",
];

export default function LeadForm() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector("textarea");
    if (textarea && textarea.value.length > 500) {
      alert("Please limit your message to 500 characters.");
      return;
    }
    setLoading(true);
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => {
      data.append(key, value as string);
    });
    try {
      await fetch("https://readdy.ai/api/form/d7934tb2hsar0k5m0hc0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="lead-form" className="py-20 md:py-28 bg-gradient-to-b from-[#F1F8F4] to-[#E8F5E9]">
      <div className="max-w-3xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="text-center mb-10">
          <span className="inline-flex items-center gap-2 text-[#1B4332] text-xs font-bold uppercase tracking-widest mb-3">
            <span className="w-2 h-2 rounded-full bg-green-500 inline-block"></span>
            Free Quote Request
          </span>
          <h2 className="text-4xl md:text-5xl font-extrabold text-[#1B4332] mb-3">
            Get Your Personalized Estimate
          </h2>
          <p className="text-gray-500 text-base md:text-lg">
            No obligation. Local pros respond within 24 hours.
          </p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl p-8 md:p-10">
          {submitted ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-[#E8F5E9] rounded-full mb-5">
                <i className="ri-checkbox-circle-line text-[#1B4332] text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1B4332] mb-2">You&apos;re All Set!</h3>
              <p className="text-gray-500 text-base max-w-sm">
                Thanks for reaching out! A local tree service professional will contact you within 24 hours with your free, no-obligation quote.
              </p>
              <button
                onClick={() => setSubmitted(false)}
                className="mt-6 text-[#1B4332] font-semibold text-sm underline cursor-pointer"
              >
                Submit another request
              </button>
            </div>
          ) : (
            <form
              data-readdy-form
              id="tree-trimming-lead-form"
              onSubmit={handleSubmit}
              className="flex flex-col gap-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {/* Full Name */}
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="name" className="text-sm font-semibold text-[#1B4332]">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    placeholder="Jane Smith"
                    className="h-12 px-4 rounded-lg border border-gray-200 text-sm text-gray-800 focus:outline-none focus:border-[#1B4332] transition-colors"
                  />
                </div>

                {/* Phone */}
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="phone" className="text-sm font-semibold text-[#1B4332]">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    placeholder="(555) 000-0000"
                    className="h-12 px-4 rounded-lg border border-gray-200 text-sm text-gray-800 focus:outline-none focus:border-[#1B4332] transition-colors"
                  />
                </div>

                {/* Zip Code */}
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="zip" className="text-sm font-semibold text-[#1B4332]">
                    Zip Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="zip"
                    name="zip"
                    type="text"
                    required
                    placeholder="e.g. 90210"
                    maxLength={10}
                    className="h-12 px-4 rounded-lg border border-gray-200 text-sm text-gray-800 focus:outline-none focus:border-[#1B4332] transition-colors"
                  />
                </div>

                {/* Service Type */}
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="service" className="text-sm font-semibold text-[#1B4332]">
                    Service Type <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <select
                      id="service"
                      name="service"
                      required
                      defaultValue=""
                      className="h-12 w-full px-4 pr-10 rounded-lg border border-gray-200 text-sm text-gray-800 bg-white focus:outline-none focus:border-[#1B4332] transition-colors appearance-none cursor-pointer"
                    >
                      <option value="" disabled>Select a service...</option>
                      {serviceOptions.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                    <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
                      <i className="ri-arrow-down-s-line"></i>
                    </div>
                  </div>
                </div>
              </div>

              {/* Comments */}
              <div className="flex flex-col gap-1.5">
                <label htmlFor="comments" className="text-sm font-semibold text-[#1B4332]">
                  Additional Details <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea
                  id="comments"
                  name="comments"
                  rows={4}
                  maxLength={500}
                  placeholder="Tell us about your trees — size, location, any concerns..."
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className="px-4 py-3 rounded-lg border border-gray-200 text-sm text-gray-800 focus:outline-none focus:border-[#1B4332] transition-colors resize-none"
                />
                <span className="text-xs text-gray-400 text-right">{charCount}/500</span>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="w-full h-14 bg-[#1B4332] hover:bg-[#2d6a4f] text-white font-bold text-base rounded-xl transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i> Submitting...
                  </>
                ) : (
                  <>
                    Get My Free Quote <i className="ri-arrow-right-line"></i>
                  </>
                )}
              </button>

              {/* Privacy */}
              <p className="text-center text-xs text-gray-400 flex items-center justify-center gap-1.5">
                <i className="ri-lock-line"></i>
                We respect your privacy. Your information is secure and never sold.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
